import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export const MarkdownStyleContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        fontSize: 16,
        lineHeight: 1.5,
        letterSpacing: "0.00938em",
        color: theme.palette.text.primary,

        // Headings using Typography variants
        "& h1": {
          ...theme.typography.h1,
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(2),
        },
        "& h2": {
          ...theme.typography.h2,
          marginTop: theme.spacing(2.5),
          marginBottom: theme.spacing(1.5),
        },
        "& h3": {
          ...theme.typography.h3,
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(1),
        },
        "& h4": {
          ...theme.typography.h4,
          marginTop: theme.spacing(1.5),
          marginBottom: theme.spacing(0.5),
        },
        "& h5": {
          ...theme.typography.h5,
          marginTop: theme.spacing(1.5),
          marginBottom: theme.spacing(0.5),
        },
        "& h6": {
          ...theme.typography.h6,
          marginTop: theme.spacing(1.5),
          marginBottom: theme.spacing(0.5),
        },

        // Paragraphs
        "& p": {
          ...theme.typography.body1,
          marginBottom: theme.spacing(1),
        },

        // Lists
        "& ul, & ol": {
          paddingLeft: theme.spacing(2),
          marginBottom: theme.spacing(1),
        },
        "& li": {
          marginBottom: theme.spacing(0.5),
        },

        // Links
        "& a": {
          color: theme.palette.primary.main,
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },

        // Inline code - GitHub style
        "& code": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(240, 246, 252, 0.15)"
              : "#f6f8fa",
          color: theme.palette.mode === "dark" ? "#f0f6fc" : "#24292f",
          padding: "0.2em 0.4em",
          borderRadius: theme.spacing(0.5),
          fontSize: "0.85em",
          fontFamily:
            "ui-monospace, SFMono-Regular, 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace",
        },

        // Code blocks - GitHub style
        "& pre": {
          backgroundColor:
            theme.palette.mode === "dark"
              ? "rgba(240, 246, 252, 0.15)"
              : "#f6f8fa",
          borderRadius: theme.spacing(0.5),
          padding: theme.spacing(2),
          overflow: "auto",
          marginBottom: theme.spacing(1),

          "& code": {
            backgroundColor: "transparent",
            padding: 0,
            fontSize: "0.85em",
            color: theme.palette.mode === "dark" ? "#f0f6fc" : "#24292f",
          },
        },

        // Blockquotes
        "& blockquote": {
          borderLeft: `4px solid ${theme.palette.divider}`,
          paddingLeft: theme.spacing(2),
          marginLeft: 0,
          marginRight: 0,
          fontStyle: "italic",
          color: theme.palette.text.secondary,
        },

        // Tables
        "& table": {
          borderCollapse: "collapse",
          width: "100%",
          marginBottom: theme.spacing(1),
        },
        "& th, & td": {
          border: `1px solid ${theme.palette.divider}`,
          padding: theme.spacing(1),
          textAlign: "left",
        },
        "& th": {
          backgroundColor: theme.palette.action.hover,
          fontWeight: theme.typography.fontWeightMedium,
        },

        // Horizontal rules
        "& hr": {
          border: "none",
          borderTop: `1px solid ${theme.palette.divider}`,
          margin: `${theme.spacing(2)} 0`,
        },

        // Images
        "& img": {
          maxWidth: "100%",
          height: "auto",
        },
      }}
    >
      {children}
    </Box>
  );
};
